import { z } from 'zod';
/**
 * Models Service
 * Module 07 - Dashboard UI
 * 
 * Handles 3D model operations
 */

import api from './api';
import { Model } from '../types';

const modelApiSchema = z.object({
  id: z.string().optional(),
  modelId: z.string().optional(),
  userId: z.string().optional(),
  name: z.string().optional(),
  originalFilename: z.string().optional(),
  originalFileName: z.string().optional(),
  status: z.enum(['uploaded', 'processing', 'ready', 'failed']).optional(),
  originalKey: z.string().optional(),
  optimizedKey: z.string().nullable().optional(),
  optimisedKey: z.string().nullable().optional(),
  thumbnailKey: z.string().nullable().optional(),
  originalFileSize: z.number().optional(),
  fileSize: z.number().optional(),
  optimizedFileSize: z.number().nullable().optional(),
  optimisedFileSize: z.number().nullable().optional(),
  originalUrl: z.string().nullable().optional(),
  optimizedUrl: z.string().nullable().optional(),
  optimisedUrl: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  processingError: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  metadata: z.record(z.any()).nullable().optional(),
  createdAt: z.string().optional(),
  uploadedAt: z.string().optional(),
  updatedAt: z.string().optional(),
  processedAt: z.string().nullable().optional(),
});

const renameResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  originalFilename: z.string(),
  updatedAt: z.string(),
});

type RawModel = z.infer<typeof modelApiSchema>;

function normaliseModel(raw: RawModel): Model {
  const model = modelApiSchema.passthrough().parse(raw);

  return {
    id: model.id || model.modelId || '',
    userId: model.userId || '',
    name: model.name || model.originalFilename || model.originalFileName || 'Untitled Model',
    status: (model.status as Model['status']) || 'processing',
    originalKey: model.originalKey || '',
    optimisedKey: model.optimisedKey || model.optimizedKey || null,
    thumbnailKey: model.thumbnailKey ?? null,
    originalFileSize: model.originalFileSize ?? model.fileSize ?? 0,
    optimisedFileSize: model.optimisedFileSize ?? model.optimizedFileSize ?? null,
    originalUrl: model.originalUrl ?? null,
    optimisedUrl: model.optimisedUrl ?? model.optimizedUrl ?? null,
    thumbnailUrl: model.thumbnailUrl ?? null,
    processingError: model.processingError ?? model.errorMessage ?? null,
    metadata: model.metadata ?? null,
    createdAt: model.createdAt ?? model.uploadedAt ?? new Date().toISOString(),
    updatedAt: model.updatedAt ?? model.processedAt ?? model.createdAt ?? new Date().toISOString(),
  };
}

/**
 * Determine the correct MIME type for a 3D model file
 * 
 * Browsers often send .glb files as application/octet-stream,
 * but our API expects model/gltf-binary or model/gltf+json
 */
function getCorrectMimeType(file: File): string {
  const extension = file.name.toLowerCase().split('.').pop();
  
  // Override browser MIME type based on file extension
  if (extension === 'glb') {
    return 'model/gltf-binary';
  }
  
  if (extension === 'gltf') {
    return 'model/gltf+json';
  }
  
  // Fallback to file's original MIME type
  return file.type || 'application/octet-stream';
}

export const modelsService = {
  /**
   * Get all models for current user
   * 
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Paginated list of models
   */
  async getModels(page: number = 1, limit: number = 20): Promise<Model[]> {
    const response = await api.get('/models', {
      params: { page, limit },
    });
    const payload = response.data;
    const rawModels: unknown[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.models)
        ? payload.models
        : [];
    return rawModels.map((m) => normaliseModel(m as RawModel));
  },

  /**
   * Get single model by ID
   * 
   * @param id - Model ID
   * @returns Model details
   */
  async getModel(id: string): Promise<Model> {
    const response = await api.get(`/models/${id}`);
    return normaliseModel(response.data as RawModel);
  },

  /**
   * Upload new 3D model file
   * 
   * @param file - GLB/GLTF file to upload
   * @param onProgress - Optional progress callback (0-100)
   * @returns Created model record
   */
  async uploadModel(
    file: File,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<Model> {
    const formData = new FormData();
    
    // CRITICAL FIX: Override MIME type to ensure correct type is sent
    // Browsers often send .glb as application/octet-stream, but API expects model/gltf-binary
    const correctMimeType = getCorrectMimeType(file);
    const correctedFile = new File([file], file.name, { type: correctMimeType });
    
    formData.append('file', correctedFile);

    // CRITICAL FIX: Remove the default Content-Type header for FormData uploads
    // FormData needs to set its own multipart/form-data with boundary
    const response = await api.post('/models', formData, {
      headers: {
        'Content-Type': undefined, // Let axios/browser auto-set multipart/form-data
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
      signal,
    });

    return normaliseModel(response.data as RawModel);
  },

  /**
   * Delete model by ID
   * 
   * @param id - Model ID to delete
   */
  async deleteModel(id: string): Promise<void> {
    await api.delete(`/models/${id}`);
  },

  /**
   * Poll model status until processing complete
   * 
   * @param id - Model ID to poll
   * @param intervalMs - Polling interval in milliseconds (default: 2000)
   * @param maxAttempts - Maximum polling attempts (default: 60)
   * @returns Final model state (ready or failed)
   */
  async pollModelStatus(
    id: string,
    intervalMs: number = 2000,
    maxAttempts: number = 60,
    signal?: AbortSignal
  ): Promise<Model> {
    let attempts = 0;
    let timer: number | undefined;
    
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error('Polling aborted'));
        return;
      }

      const stop = (error?: unknown) => {
        if (timer) {
          window.clearTimeout(timer);
        }
        if (error) {
          reject(error);
        }
      };

      const onAbort = () => stop(new Error('Polling aborted'));
      signal?.addEventListener('abort', onAbort, { once: true });

      const poll = async () => {
        try {
          attempts++;
          
          const model = await this.getModel(id);
          
          // Check if processing complete
          if (model.status === 'ready' || model.status === 'failed') {
            resolve(model);
            return;
          }
          
          // Check max attempts
          if (attempts >= maxAttempts) {
            stop(new Error('Maximum polling attempts reached'));
            return;
          }
          
          // Continue polling
          timer = window.setTimeout(poll, intervalMs);
        } catch (error) {
          stop(error);
        }
      };
      
      poll();
    });
  },

  /**
   * Get storage usage statistics
   *
   * @returns Storage usage info
   */
  async getStorageUsage(): Promise<{
    used: number;
    quota: number;
    percentage: number;
  }> {
    const response = await api.get('/models/storage/usage');
    return response.data;
  },

  /**
   * Rename a model
   *
   * @param id - Model ID
   * @param name - New display name (1–100 chars, trimmed server-side)
   * @returns Partial model update with new name and updatedAt
   */
  async renameModel(
    id: string,
    name: string
  ): Promise<Pick<Model, 'id' | 'name' | 'updatedAt'>> {
    const response = await api.patch(`/models/${id}`, { name });
    const parsed = renameResponseSchema.parse(response.data);
    return {
      id: parsed.id,
      name: parsed.name,
      updatedAt: parsed.updatedAt,
    };
  },
};
